import { useState} from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { logIn } from "../api/apiUsers";
import supabase from "../api/supabase";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EyeIcon, EyeOffIcon } from "lucide-react";

const LOCAL_STORAGE_KEY = "userProfile";
const EXPIRY_KEY = "userProfileExpiry";
const EXPIRY_DURATION = 60 * 60 * 1000; // 1-hour expiration duration

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Handle login mutation
  const mutation = useMutation({
    mutationFn: logIn,
    onSuccess: async (data) => {
      if (data && data.user) {
        queryClient.invalidateQueries("user");
        try {
          const { data: sessionData, error: sessionError } =
            await supabase.auth.getSession();

          if (sessionError || !sessionData.session) {
            console.error("No active session found.");
            toast.error("No active session found.");
            return;
          }

          const { data: userData, error: userError } =
            await supabase.auth.getUser();

          if (userError || !userData?.user) {
            console.error("Error fetching authenticated user:", userError);
            toast.error("Error fetching authenticated user.");
            return;
          }

          const { data: profile, error: profileError } = await supabase
            .from("employees")
            .select("empRole, empUserId, id, empAvatar, empName, empEmail")
            .eq("empUserId", userData.user.id)
            .single();

          if (profileError || !profile) {
            console.error("Error fetching user profile:", profileError);
            toast.error("Error fetching user profile.");
            return;
          }

          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(profile));
          localStorage.setItem(EXPIRY_KEY, Date.now() + EXPIRY_DURATION);

          toast.success("Login successful! Redirecting...");
          const redirectBasedOnRole = (empRole, userId) => {
            if (empRole && empRole.length === 1 && empRole[0] === "Employee") {
              navigate(`/profile/${userId}`);
            } else {
              navigate("/dashboard");
            }
          };
          redirectBasedOnRole(profile.empRole, userData.user.id);
        } catch (error) {
          console.error("Unexpected error during profile fetch:", error);
          toast.error("An unexpected error occurred.");
        }
      }
    },
    onError: (error) => {
      toast.error(error.message);
      reset();
    },
  });

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  // Handle forget password submission
  const handleForgetPasswordSubmit = async (data) => {
    const { email } = data;
    const redirectTo = `${window.location.origin}/auth_passwordreset`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) {
      console.error("Password reset failed:", error.message, error);
      const isDeliveryFailure =
        error.status === 500 || error.code === "unexpected_failure";
      toast.error(
        isDeliveryFailure
          ? "Could not send reset email. Check Auth SMTP settings in Supabase."
          : error.message || "Password reset failed. Please try again."
      );
    } else {
      toast.success("Password reset email sent! Please check your inbox.");
      setIsResetPassword(false);
      reset();
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="w-full max-w-[400px] space-y-6 rounded-lg bg-white p-8 shadow-lg m-4">
        <div className="space-y-2 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/puritylogo_main.png" 
              alt="Purity Logo" 
              className="w-40 h-auto"
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isResetPassword ? "Reset Password" : "Sign In to your account"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isResetPassword
              ? "Enter your email to get password reset link"
              : "Enter your valid email and password"}
          </p>
        </div>

        <form
          onSubmit={handleSubmit(
            isResetPassword ? handleForgetPasswordSubmit : onSubmit
          )}
          className="space-y-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Email
            </label>
            <Input
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address",
                },
              })}
              
              placeholder={isResetPassword ? "you@example.com" : "name@example.com"}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          {!isResetPassword && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setIsResetPassword(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  {...register("password", { required: "Password is required" })}
                 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password.message}</p>
              )}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={mutation.isPending}
          >
            {isResetPassword
              ? "Send Reset Link"
              : mutation.isPending
              ? "Signing in..."
              : "Sign in"}
          </Button>
        </form>

        <div className="text-center text-sm">
          {isResetPassword ? (
            <button
              type="button"
              onClick={() => setIsResetPassword(false)}
              className="text-primary hover:underline"
            >
              Back to login
            </button>
          ) : (
            <>
              Don&apos;t have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  toast.success("Coming Soon!", {
                    icon: '🚀',
                    duration: 2000,
                    position: 'top-center',
                    style: {
                      backgroundColor: '#333',
                      color: '#fff'
                    }
                  });
                }}
                className="text-primary hover:underline"
              >
                Sign up
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
